/**
 * Forgot Password Page — Triggers Supabase Auth password reset link via Email.
 */

import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { Mail, ArrowRight, AlertCircle, CheckCircle2, KeyRound } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { siteConfig } from "@/config/site";

export const Route = createFileRoute("/account/forgot-password")({
  head: () => ({
    meta: [
      { title: `Forgot Password — ${siteConfig.name}` },
      // noindex,follow — see the note in account/login.tsx: crawl allowed, indexing refused.
      { name: "robots", content: "noindex, follow" },
    ],
  }),
  component: ForgotPasswordPage,
});

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successNotice, setSuccessNotice] = useState<string | null>(null);

  const extractResetErrorMessage = (error: unknown): string => {
    if (!error) return "Failed to send password reset email. Please try again.";
    if (typeof error === "string" && error.trim() && error !== "{}") {
      return error;
    }
    const e = error as { message?: string; error_description?: string; status?: number };
    const msg = (e.message || e.error_description || "").toLowerCase();
    if (msg.includes("rate limit") || msg.includes("too many requests") || e.status === 429) {
      return "Email rate limit reached (Supabase allows a maximum of 3-4 emails per hour on default tier). Please wait a few minutes or reset your password directly in the Supabase Dashboard.";
    }
    if (e.message && typeof e.message === "string" && e.message.trim() && e.message !== "{}") {
      return e.message;
    }
    return "Failed to send password reset email. Please try again later.";
  };

  const handleResetRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg(null);
    setSuccessNotice(null);

    if (!email.trim()) {
      setErrorMsg("Please enter your registered email address.");
      setLoading(false);
      return;
    }

    try {
      // Constructs recovery redirect URL dynamically based on current environment
      const redirectUrl = `${window.location.origin}/account/reset-password`;

      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: redirectUrl,
      });

      if (error) {
        setErrorMsg(extractResetErrorMessage(error));
      } else {
        setSuccessNotice(
          "Password reset link has been sent to your email address. Please check your inbox and click the link to reset your password.",
        );
      }
    } catch (err: unknown) {
      setErrorMsg(extractResetErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="section-container pb-28 pt-24 md:pt-28 max-w-md mx-auto">
      <div className="text-center">
        <p className="eyebrow">Account Recovery</p>
        <h1 className="mt-3 font-display text-3xl sm:text-4xl">Reset Your Password</h1>
        <p className="mt-2 text-xs text-muted-foreground">
          Enter your email address and we'll send you a link to reset your password.
        </p>
      </div>

      <div className="mt-8 border bg-card p-6 sm:p-8">
        {errorMsg && (
          <div className="mb-5 flex items-center gap-2 text-xs text-destructive bg-destructive/10 p-3 rounded border border-destructive/20">
            <AlertCircle className="h-4 w-4 shrink-0 text-destructive" />
            <span>{errorMsg}</span>
          </div>
        )}

        {successNotice ? (
          <div className="space-y-6 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-950 text-emerald-600">
              <CheckCircle2 className="h-6 w-6" />
            </div>
            <p className="text-xs leading-relaxed text-muted-foreground">{successNotice}</p>
            <Link
              to="/account/login"
              className="inline-flex min-h-11 w-full items-center justify-center gap-2 bg-secondary text-xs font-semibold text-foreground uppercase tracking-wider transition-colors hover:bg-secondary/80"
            >
              Back to Sign In
            </Link>
          </div>
        ) : (
          <form onSubmit={handleResetRequest} className="space-y-5">
            <div>
              <label htmlFor="email" className="eyebrow block">
                Email Address
              </label>
              <div className="relative mt-2">
                <Mail className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="customer@example.com"
                  className="min-h-11 w-full border border-border bg-background pl-10 pr-4 text-xs outline-none focus:border-primary"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="flex min-h-12 w-full items-center justify-center gap-2 bg-primary text-xs tracking-[0.2em] uppercase text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {loading ? "Sending Link..." : "Send Reset Link"}
              <ArrowRight className="h-4 w-4" />
            </button>

            <p className="text-center text-xs text-muted-foreground pt-4 border-t mt-6">
              Remember your password?{" "}
              <Link to="/account/login" className="text-foreground underline font-medium">
                Sign In
              </Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
